import React, { useState } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Sparkles, Paperclip, Image as ImageIcon, ExternalLink, Download, X } from 'lucide-react';

const isImageUrl = (url = '') =>
  /\.(jpg|jpeg|png|gif|webp|heic|heif|bmp|svg)(\?|$)/i.test(url);

/**
 * Blocco evidenziato con le personalizzazioni richieste dal cliente su un item.
 * Mostra:
 *  - customizationValues: mappa {label|index -> text}
 *  - customerAttachments: mappa {index -> {url, name}} → thumbnail immagine
 *    cliccabile (lightbox) o link file scaricabile
 */
export const OrderItemCustomizationBlock = ({ item, productCustomizations = [] }) => {
  const [lightbox, setLightbox] = useState(null);

  const customizationValues = item?.customizationValues || {};
  const customerAttachments = item?.customerAttachments || {};
  const selectedCustomizations = item?.selectedCustomizations || {};

  const textEntries = Object.entries(customizationValues).filter(
    ([, v]) => v !== null && v !== undefined && String(v).trim() !== ''
  );
  const fileEntries = Object.entries(customerAttachments).filter(
    ([, v]) => v && (v.url || typeof v === 'string')
  );
  // Personalizzazioni selezionate ma senza dati compilati (caso legacy o
  // cliente che ha spuntato il checkbox senza aggiungere info)
  const selectedButEmpty = Object.entries(selectedCustomizations)
    .filter(([, v]) => v === true)
    .map(([k]) => k)
    .filter((k) => !customizationValues[k] && !customerAttachments[k]);

  if (textEntries.length === 0 && fileEntries.length === 0 && selectedButEmpty.length === 0) {
    return null;
  }

  // Prova a mappare l'indice della personalizzazione al label del prodotto,
  // se disponibile (customization_options.customizations dell'oggetto product)
  const getLabelFor = (key) => {
    const idx = parseInt(key, 10);
    if (!Number.isNaN(idx) && productCustomizations[idx]?.label) {
      return productCustomizations[idx].label;
    }
    return key;
  };

  return (
    <>
      <div
        className="mt-3 rounded-lg border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 p-3 sm:p-4"
        data-testid="order-item-customization-block"
      >
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-full bg-amber-200 text-amber-800 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </div>
          <p className="font-semibold text-amber-900 text-sm">
            Personalizzazione richiesta dal cliente
          </p>
        </div>

        {/* Testi/valori */}
        {textEntries.length > 0 && (
          <div className="space-y-2 mb-3">
            {textEntries.map(([key, value]) => (
              <div key={`t-${key}`} className="bg-white rounded-md border border-amber-200 p-2.5">
                <p className="text-xs font-medium text-amber-800 uppercase tracking-wider mb-1">
                  {getLabelFor(key)}
                </p>
                <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">
                  {String(value)}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Personalizzazioni selezionate ma senza contenuto compilato */}
        {selectedButEmpty.length > 0 && textEntries.length === 0 && fileEntries.length === 0 && (
          <div className="bg-white rounded-md border-2 border-amber-400 border-dashed p-3 mb-3">
            <p className="text-sm text-amber-900 leading-relaxed">
              <strong>Il cliente ha richiesto una personalizzazione</strong> ma non ha
              fornito dettagli specifici. Contattalo in chat per raccogliere le
              informazioni necessarie prima di iniziare la lavorazione.
            </p>
          </div>
        )}

        {/* Allegati (immagini + file) */}
        {fileEntries.length > 0 && (
          <div>
            <div className="flex items-center gap-1.5 text-xs font-medium text-amber-800 uppercase tracking-wider mb-2">
              <Paperclip className="w-3.5 h-3.5" />
              Allegati ({fileEntries.length})
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {fileEntries.map(([key, att]) => {
                const url = typeof att === 'string' ? att : att.url;
                const name = typeof att === 'string' ? url.split('/').pop() : (att.name || url.split('/').pop());
                const isImg = isImageUrl(url);
                return (
                  <div
                    key={`a-${key}`}
                    className="bg-white rounded-md border border-amber-200 overflow-hidden"
                  >
                    {isImg ? (
                      <button
                        type="button"
                        onClick={() => setLightbox({ url, name })}
                        className="w-full aspect-square relative group"
                        data-testid={`attachment-preview-${key}`}
                        title="Clicca per ingrandire"
                      >
                        <img
                          src={url}
                          alt={name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                          <ExternalLink className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </button>
                    ) : (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center justify-center aspect-square p-3 hover:bg-amber-50 transition-colors"
                        data-testid={`attachment-file-${key}`}
                      >
                        <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center mb-2">
                          <Paperclip className="w-5 h-5" />
                        </div>
                        <span className="text-xs text-slate-600 text-center break-all line-clamp-2">
                          {name}
                        </span>
                      </a>
                    )}
                    <div className="px-2 py-1.5 border-t border-amber-100 flex items-center justify-between gap-2">
                      <span className="text-xs text-slate-500 truncate flex-1" title={name}>
                        {name}
                      </span>
                      <a
                        href={url}
                        download={name}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                        title="Scarica"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox immagine */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent
          className="max-w-5xl p-0 bg-black/95 border-0"
          data-testid="attachment-lightbox"
        >
          {lightbox && (
            <div className="relative flex flex-col">
              <button
                onClick={() => setLightbox(null)}
                className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors"
                aria-label="Chiudi"
              >
                <X className="w-5 h-5" />
              </button>
              <img
                src={lightbox.url}
                alt={lightbox.name}
                className="max-w-full max-h-[85vh] object-contain mx-auto"
              />
              <div className="px-4 py-3 bg-black/70 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-white/90 text-sm min-w-0">
                  <ImageIcon className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{lightbox.name}</span>
                </div>
                <a
                  href={lightbox.url}
                  download={lightbox.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 text-white text-sm transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Scarica
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default OrderItemCustomizationBlock;
